import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Popconfirm,
  message,
  ConfigProvider,
  Modal,
} from "antd";
import { PlusOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import "./chatEmojis.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import ImageUpload from "../../components/imageUpload/imageUpload";

const ChatEmojis = () => {
  const [emojis, setEmojis] = useState([]);
  const [filteredEmojis, setFilteredEmojis] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");

  useEffect(() => {
    getEmojis();
  }, []);

  const getEmojis = () => {
    const onSuccess = (res) => {
      if (res.status === 200) {
        const data =
          res.data?.data?.data ||
          res.data?.data ||
          res.data ||
          [];

        if (!Array.isArray(data)) {
          message.error("Invalid response format for chat emojis");
          return;
        }

        setEmojis(data);
        applySearch(data, searchText);
      }
    };

    callApi(
      "GET",
      routes.getAllChatEmojis,
      null,
      setIsLoading,
      onSuccess,
      (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load chat emojis";
        message.error(errorMsg);
      }
    );
  };

  const applySearch = (source, search) => {
    const value = search.trim().toLowerCase();
    if (!value) {
      setFilteredEmojis(source);
      return;
    }

    const filtered = source.filter((item) => {
      const emoji = (item.emoji || "").toLowerCase();
      return emoji.includes(value);
    });

    setFilteredEmojis(filtered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    applySearch(emojis, value);
  };

  const openAddModal = () => {
    setUploadedImageUrl("");
    setModalOpen(true);
  };

  const closeAddModal = () => {
    setModalOpen(false);
    setUploadedImageUrl("");
  };

  const handleCreateFromModal = () => {
    if (!uploadedImageUrl.trim()) {
      message.error("Please select and upload an image first");
      return;
    }

    const body = {
      emoji: uploadedImageUrl.trim(),
    };

    const onSuccess = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("Chat emoji added successfully");
        closeAddModal();
        getEmojis();
      } else {
        message.warning(`Create completed with status ${res.status}`);
      }
    };

    callApi(
      "POST",
      routes.createChatEmoji,
      body,
      setCreating,
      onSuccess,
      (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add chat emoji";
        message.error(errorMsg);
      }
    );
  };

  const handleDelete = (emoji) => {
    const id = emoji._id || emoji.id;
    if (!id) {
      message.error("Invalid emoji id");
      return;
    }

    const onSuccess = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("Chat emoji deleted successfully");
        getEmojis();
      } else {
        message.warning(`Delete completed with status ${res.status}`);
        getEmojis();
      }
    };

    callApi(
      "DELETE",
      `${routes.deleteChatEmoji}/${id}`,
      null,
      setIsLoading,
      onSuccess,
      (error) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete chat emoji";
        message.error(errorMsg);
      }
    );
  };

  const columns = [
    {
      title: "No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: "Emoji",
      dataIndex: "emoji",
      key: "emoji",
      render: (value) => {
        const isUrl = typeof value === "string" && value.startsWith("http");
        if (isUrl) {
          return (
            <img
              src={value}
              alt="emoji"
              style={{ width: 32, height: 32, objectFit: "contain" }}
            />
          );
        }
        return (
          <span style={{ fontSize: 24 }}>
            {value || "N/A"}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Delete emoji"
          description="Are you sure you want to delete this emoji?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => handleDelete(record)}
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="chat-emojis-container">
        <Loader loading={isLoading} />
        <div className="chat-emojis-header">
          <h1 className="chat-emojis-title">Chat Emojis</h1>
          <p className="chat-emojis-subtitle">
            Pick an image from your computer. It will be uploaded, then saved as a chat emoji.
          </p>
        </div>

        <div className="chat-emojis-content">
          <Card className="chat-emojis-card">
            <div className="chat-emojis-actions">
              <Input
                placeholder="Search emojis by string..."
                prefix={<SearchOutlined />}
                allowClear
                value={searchText}
                onChange={handleSearchChange}
                style={{ maxWidth: 300 }}
              />

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddModal}
              >
                Add Emoji
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={filteredEmojis}
              rowKey={(record) => record._id || record.id || record.emoji}
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
              }}
              className="chat-emojis-table"
            />
          </Card>
        </div>

        <Modal
          title="Add Chat Emoji"
          open={modalOpen}
          onCancel={closeAddModal}
          footer={[
            <Button key="cancel" onClick={closeAddModal}>
              Cancel
            </Button>,
            <Button
              key="create"
              type="primary"
              loading={creating}
              disabled={!uploadedImageUrl}
              onClick={handleCreateFromModal}
            >
              Add Emoji
            </Button>,
          ]}
          destroyOnClose
          width={400}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            Select an image from your computer. It will be uploaded, then added as a chat emoji.
          </p>
          <ImageUpload
            value={uploadedImageUrl}
            onChange={setUploadedImageUrl}
            maxSize={5}
            accept="image/*"
          />
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default ChatEmojis;

